/**
 * The world's airports — now authored geographically (lat/lon) and projected
 * into the sim's flat local NED frame.
 *
 * `RunwayDef` (below) is still the single flat-NED source of truth the renderer
 * and the ILS navigation consume; it is now DERIVED from geographic data via the
 * projection in Geo.ts rather than hand-built. The primary field (Napa / KAPC,
 * runway 24) keeps its exact original NED — the datum is chosen so the runway-24
 * midpoint maps to NED (0,0) and the projection round-trips exactly — so the
 * existing training environment is behaviourally identical. A second real field
 * (Santa Rosa / KSTS, ~22 NM NW) demonstrates multi-airport placement.
 *
 * All local positions remain in the world NED horizontal plane (north, east) in
 * metres; altitude is MSL. Heading/elevation of KAPC runway 24 are kept at the
 * legacy 240°/0 ft for continuity (real KAPC 24 is ~237°/35 ft) — trivially
 * updated in the RunwayGeo below.
 */
import { GeoPoint, LocalProjection } from './Geo';
import { World, AirportGeo, RunwayGeo } from './World';

const DEG = Math.PI / 180;

export interface RunwayDef {
  ident: string;
  headingDeg: number; // landing direction, true
  length: number; // m
  width: number; // m
  elevation: number; // m MSL of runway surface
  // Unit vector along the landing rollout direction, in (north, east)
  dir: { n: number; e: number };
  // Threshold you land toward on this runway ident (approach end), (n, e)
  threshold: { n: number; e: number };
  // ILS
  ils: {
    freq: number; // MHz (display only for now)
    gsAngleDeg: number; // glideslope angle
    locWidthDeg: number; // full-scale (2-dot) localizer half-width
    gsWidthDeg: number; // full-scale (2-dot) glideslope half-width
  };
}

// --- Datum -----------------------------------------------------------------
// The projection datum is the geo point that maps to local NED (0,0) — i.e.
// today's runway-24 midpoint. Seeded with Napa County (KAPC)'s real location so
// the world is anchored at real coordinates and a second real field sits at its
// true relative position. Elevation 0 keeps the ground at MSL 0 for continuity.
const DATUM: GeoPoint = { lat: 38.2132, lon: -122.2807, elevation: 0 };
const proj = new LocalProjection(DATUM);

// --- KAPC runway 24 (authored to reproduce the legacy NED exactly) ---------
const RWY24_HEADING = 240;
const RWY24_HALF = 750; // half runway length (m)
const dir24 = { n: Math.cos(RWY24_HEADING * DEG), e: Math.sin(RWY24_HEADING * DEG) };
// Legacy approach-end threshold: up-track from the midpoint (the NED origin).
const threshold24NE = { n: -dir24.n * RWY24_HALF, e: -dir24.e * RWY24_HALF };

const RUNWAY_24_GEO: RunwayGeo = {
  ident: '24',
  headingDeg: RWY24_HEADING,
  length: RWY24_HALF * 2,
  width: 45,
  // localToGeo of the legacy NED threshold at elevation 0 — geoToLocal of this
  // returns threshold24NE to float precision (exact-inverse projection).
  thresholdGeo: proj.localToGeo(threshold24NE, 0),
  ils: { freq: 108.9, gsAngleDeg: 3.0, locWidthDeg: 2.5, gsWidthDeg: 0.7 },
};

const KAPC: AirportGeo = {
  ident: 'KAPC',
  name: 'Napa County',
  arp: DATUM,
  runways: [RUNWAY_24_GEO],
  navaids: [
    // Scaggs Island VORTAC — seam only (no instrument reads it yet).
    { ident: 'SGD', type: 'VORDME', freq: 112.1, position: { lat: 38.181, lon: -122.373, elevation: 0 } },
  ],
};

// --- KSTS (Santa Rosa) — real ARP, one ILS runway, non-zero elevation ------
const STS_ELEV = 38; // m MSL (~125 ft)
const STS_ARP: GeoPoint = { lat: 38.509, lon: -122.813, elevation: STS_ELEV };
const RWY32_HEADING = 320;
const RWY32_LEN = 1800;
const dir32 = { n: Math.cos(RWY32_HEADING * DEG), e: Math.sin(RWY32_HEADING * DEG) };
// Author the runway centred on the real ARP: approach-end (32) threshold is
// half the length up-track (toward the SE) from the ARP, projected to geo.
const stsArpNE = proj.geoToLocal(STS_ARP);
const threshold32NE = {
  n: stsArpNE.n - dir32.n * (RWY32_LEN / 2),
  e: stsArpNE.e - dir32.e * (RWY32_LEN / 2),
};

const KSTS: AirportGeo = {
  ident: 'KSTS',
  name: 'Santa Rosa',
  arp: STS_ARP,
  runways: [
    {
      ident: '32',
      headingDeg: RWY32_HEADING,
      length: RWY32_LEN,
      width: 45,
      thresholdGeo: proj.localToGeo(threshold32NE, STS_ELEV),
      ils: { freq: 109.7, gsAngleDeg: 3.0, locWidthDeg: 2.5, gsWidthDeg: 0.7 },
    },
  ],
  navaids: [
    { ident: 'STS', type: 'VORDME', freq: 113.0, position: { lat: 38.508, lon: -122.812, elevation: STS_ELEV } },
  ],
};

/** The world model: both airports, projected into one shared local NED plane. */
export const WORLD = new World(DATUM, [KAPC, KSTS]);

/** Runway 24 (KAPC) — the derived flat-NED runway, identical to the legacy one. */
export const RUNWAY_24: RunwayDef = WORLD.airport('KAPC')!.runways[0];

// --- Airport-aware start-pose helpers --------------------------------------
const FT_TO_M = 0.3048;

export interface StartPose {
  n: number;
  e: number;
  altitudeFt: number; // MSL
  headingDeg: number;
  speedKts: number;
}

/** On-the-ground pose: lined up on a runway's threshold, ready for takeoff. */
export function takeoffPose(rwy: RunwayDef): { n: number; e: number; headingDeg: number } {
  return { n: rwy.threshold.n, e: rwy.threshold.e, headingDeg: rwy.headingDeg };
}

/**
 * In-flight intercept start: `nm` out from the threshold on the extended
 * centreline at `heightFt` ABOVE the runway, inbound in level cruise.
 */
export function approachStartFor(
  rwy: RunwayDef, nm: number, heightFt: number, speedKts = 100,
): StartPose {
  const dist = nm * 1852;
  return {
    n: rwy.threshold.n - rwy.dir.n * dist,
    e: rwy.threshold.e - rwy.dir.e * dist,
    altitudeFt: rwy.elevation / FT_TO_M + heightFt,
    headingDeg: rwy.headingDeg,
    speedKts,
  };
}

/**
 * "On final" start: established on a runway's ILS glideslope at `heightFt` above
 * the runway — distance-out follows directly from the published GS angle.
 */
export function finalApproachStartFor(
  rwy: RunwayDef, heightFt: number, speedKts = 100,
): StartPose {
  const gsRad = rwy.ils.gsAngleDeg * DEG;
  const dist = (heightFt * FT_TO_M) / Math.tan(gsRad);
  return {
    n: rwy.threshold.n - rwy.dir.n * dist,
    e: rwy.threshold.e - rwy.dir.e * dist,
    altitudeFt: rwy.elevation / FT_TO_M + heightFt,
    headingDeg: rwy.headingDeg,
    speedKts,
  };
}

// --- Legacy exports (KAPC runway 24) — unchanged values --------------------
/** Starting pose: lined up on runway 24 threshold, ready for takeoff. */
export const START_POSE = takeoffPose(RUNWAY_24);

/** In-flight start: 4 NM out on the runway-24 approach at 1000 ft, level. */
export const APPROACH_START = approachStartFor(RUNWAY_24, 4, 1000, 100);

/** "On final" start on the runway-24 ILS glideslope at the given MSL height. */
export function finalApproachStart(altitudeFt: number, speedKts = 100): StartPose {
  return finalApproachStartFor(RUNWAY_24, altitudeFt, speedKts);
}
